import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type { CotizacionDTO, CotizacionItemDTO, EmpresaDTO, FormaPago, WrapperResponse } from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import { currentYear, daysFromToday } from '../../../utils/dates'
import { parseAmount } from '../../../utils/numbers'
import {
    aseguradoraLabel,
    errorMessage,
    FORMA_PAGO,
    formatSpokenDate,
    initials,
    money,
    planValueFields,
    quoteInsured,
    ramoFields,
    sortedValues,
    statusKey,
} from '../common/quote-common'
import type { QuoteDetailValue } from '../common/quote-common'

export type QuoteViewerSocial = 'facebook' | 'youtube' | 'tiktok' | 'twitter' | 'web'

const LOAD_ERROR = 'No pudimos cargar la cotización. Verifica que el enlace esté completo.'
const SOCIALS: Array<{ key: QuoteViewerSocial; label: string }> = [
    { key: 'facebook', label: 'Facebook' },
    { key: 'youtube', label: 'YouTube' },
    { key: 'tiktok', label: 'TikTok' },
    { key: 'twitter', label: 'X / Twitter' },
    { key: 'web', label: 'Sitio web' },
]

/** Lo que cada ramo busca proteger, para el título de bienvenida. */
const RAMO_GOAL: Record<string, string> = {
    vida: 'proteger a tu familia',
    salud: 'cuidar tu salud',
    auto: 'proteger tu auto',
    diversos: 'proteger tu patrimonio',
}

const NUMERIC_TEXT = /^\d[\d,]*(\.\d*)?$/
const MONEY_FIELDS = ['sumaAsegurada', 'deducible', 'topeCoaseguro']

const text = (value?: string | null) => String(value || '').trim()

const digitsOf = (value?: string | null) => text(value).replace(/\D/g, '')

const telHref = (value?: string | null) => {
    const digits = digitsOf(value)
    return digits ? `tel:+${digits}` : null
}

/** Los teléfonos de México se capturan a 10 dígitos; WhatsApp necesita la lada del país. */
const whatsappNumber = (value?: string | null) => {
    const digits = digitsOf(value)
    if (!digits) return null
    return digits.length === 10 ? `52${digits}` : digits
}

const toEmpresa = (empresa: EmpresaDTO | null) => {
    if (!empresa) return null
    const nombre = text(empresa.razonSocial)
    return {
        nombre,
        iniciales: initials(nombre),
        direccion: text(empresa.direccion),
        logoUrl: text(empresa.logoUrl) || null,
        telefonos: [empresa.tel1, empresa.tel2].map(text).filter(Boolean).map((tel) => ({ label: tel, href: telHref(tel) })),
        correos: [empresa.correo1, empresa.correo2].map(text).filter(Boolean),
        redes: SOCIALS.map((item) => ({ ...item, href: text(empresa[item.key]) })).filter((item) => item.href),
        whatsapp: whatsappNumber(empresa.tel1) || whatsappNumber(empresa.tel2),
    }
}

export type QuoteViewerEmpresa = NonNullable<ReturnType<typeof toEmpresa>>

const vigenciaView = (fecha?: string | null) => {
    const spoken = formatSpokenDate(fecha)
    const days = daysFromToday(fecha)
    if (!spoken || days == null) return { label: '', expired: false }
    if (days < 0) return { label: `Venció el ${spoken}`, expired: true }
    if (days === 0) return { label: `Vence hoy · ${spoken}`, expired: false }
    return { label: `Válida ${days} ${days === 1 ? 'día' : 'días'} más · hasta el ${spoken}`, expired: false }
}

const planValueText = (key: string, value: string, moneda: string) => {
    const clean = text(value)
    if (MONEY_FIELDS.includes(key) && NUMERIC_TEXT.test(clean)) {
        const amount = parseAmount(clean)
        return money(amount, moneda, Number.isInteger(amount) ? 0 : 2)
    }
    return clean
}

interface OptionPlan {
    total: number
    primer: number
    sub: number
    pagos: number
}

interface ViewerOption {
    id: number
    cia: string
    logo: string | null
    plan: string
    moneda: string
    suma: string
    campos: QuoteDetailValue[]
    caracteristicas: QuoteDetailValue[]
    coberturas: QuoteDetailValue[]
    planes: Partial<Record<FormaPago, OptionPlan>>
}

const toOption = (item: CotizacionItemDTO, ramo?: string | null): ViewerOption => {
    const moneda = item.moneda || 'MXN'
    const planes: ViewerOption['planes'] = {}
    ;(item.planesPago || []).forEach((plan) => {
        if (!plan.formaPago || planes[plan.formaPago]) return
        planes[plan.formaPago] = {
            total: Number(plan.total || 0),
            primer: Number(plan.primaPrimerRecibo || 0),
            sub: Number(plan.primaReciboSub || 0),
            pagos: FORMA_PAGO.find((option) => option.key === plan.formaPago)?.parcialidades || 1,
        }
    })
    return {
        id: item.id,
        cia: aseguradoraLabel(item.aseguradora) || 'Aseguradora',
        logo: item.aseguradora?.logoM || null,
        plan: text(item.plan) || 'Plan',
        moneda,
        suma: planValueText('sumaAsegurada', item.sumaAsegurada || '', moneda),
        campos: planValueFields(ramo)
            .map((field) => ({ key: field.key, descripcion: field.label, valor: planValueText(field.key, item[field.key] || '', moneda) }))
            .filter((field) => field.valor),
        caracteristicas: sortedValues(item.caracteristicas),
        coberturas: sortedValues(item.coberturas),
        planes,
    }
}

export interface QuoteViewerPrice {
    precio: string
    per: string
    nota: string
    total: number | null
    /** `false` si la propuesta no ofrece la forma de pago elegida; entonces no se puede seleccionar. */
    available: boolean
}

const priceOf = (option: ViewerOption, frecuencia: FormaPago | null): QuoteViewerPrice => {
    const plan = frecuencia ? option.planes[frecuencia] : undefined
    if (!frecuencia) return { precio: '—', per: '', nota: 'Pregunta a tu asesor por el precio', total: null, available: true }
    const label = FORMA_PAGO.find((item) => item.key === frecuencia)?.label.toLowerCase() || frecuencia
    if (!plan) return { precio: '—', per: '', nota: `Esta opción no tiene pago ${label}`, total: null, available: false }
    const m = (value: number) => money(value, option.moneda, Number.isInteger(value) ? 0 : 2)
    if (frecuencia === 'contado') return { precio: m(plan.total), per: 'pago único', nota: 'Un solo pago por toda la vigencia', total: plan.total, available: true }
    const nota = plan.primer === plan.sub
        ? `${plan.pagos} pagos de ${m(plan.sub)}`
        : `${plan.pagos} pagos · el 1.º de ${m(plan.primer)} y ${plan.pagos - 1} de ${m(plan.sub)}`
    return { precio: m(plan.total), per: 'en total', nota, total: plan.total, available: true }
}

/** Iconos y tonos de las tarjetas de "Así te protege", en orden de aparición. */
export const BENEFIT_TONES = [
    'bg-k-accent-soft text-k-accent-ink',
    'bg-tone-teal text-tone-teal-ink',
    'bg-tone-orange text-tone-orange-ink',
    'bg-tone-pink text-tone-pink-ink',
    'bg-tone-blue text-tone-blue-ink',
    'bg-tone-purple text-tone-purple-ink',
]

export const useQuoteViewerModel = () => {
    const { uuid } = useParams()
    const { search } = useLocation()
    const userUuid = new URLSearchParams(search).get('uuid')
    const [cotizacion, setCotizacion] = useState<CotizacionDTO | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [frecuenciaPick, setFrecuenciaPick] = useState<FormaPago | null>(null)
    useEffect(() => {
        if (!uuid) return undefined
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const query = userUuid ? `?uuid=${encodeURIComponent(userUuid)}` : ''
                const response = await APIInvoke.invokeGET(`/cotizaciones/uuid/${encodeURIComponent(uuid)}${query}`)
                const payload: WrapperResponse<CotizacionDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(LOAD_ERROR)
                setCotizacion(payload.body)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [uuid, userUuid])

    const ramo = cotizacion?.ramo || ''
    const options = useMemo(() => (cotizacion?.items || []).map((item) => toOption(item, ramo)), [cotizacion, ramo])
    const empresa = toEmpresa(cotizacion?.empresa || null)
    const contratante = cotizacion?.cliente || cotizacion?.prospecto || null
    const nombre = text(contratante?.nombres).split(/\s+/)[0] || ''
    const numero = cotizacion?.noCotizacion != null ? String(cotizacion.noCotizacion) : '—'
    const vigencia = vigenciaView(cotizacion?.fecVencimiento)

    const frecuencias = FORMA_PAGO.filter((forma) => options.some((option) => option.planes[forma.key]))
    const frecuencia = frecuenciaPick && frecuencias.some((forma) => forma.key === frecuenciaPick)
        ? frecuenciaPick
        : (frecuencias.find((forma) => forma.key === 'contado') || frecuencias[0])?.key || null
    const frecuenciaLabel = FORMA_PAGO.find((forma) => forma.key === frecuencia)?.label || ''

    const prices = options.map((option) => priceOf(option, frecuencia))
    const cheapestIndex = prices.reduce<number | null>((best, price, index) => {
        if (price.total == null) return best
        if (best == null || (prices[best].total ?? Infinity) > price.total) return index
        return best
    }, null)

    /** Si la opción elegida no ofrece la forma de pago actual, se cambia a la más económica que sí la tenga. */
    const pickedIndex = options.findIndex((option) => option.id === selectedId)
    const resolvedIndex = pickedIndex >= 0 && prices[pickedIndex].available
        ? pickedIndex
        : cheapestIndex ?? Math.max(0, prices.findIndex((price) => price.available))
    const selected = options[resolvedIndex] || null
    const selectedPrice = prices[resolvedIndex] || null

    const sumaLabel = statusKey(ramo) === 'vida' ? 'Tu familia recibe' : 'Suma asegurada'
    const goal = RAMO_GOAL[statusKey(ramo)] || 'protegerte'
    const titleCount = options.length === 1 ? 'tu propuesta' : `${options.length} opciones`
    const title = `${nombre ? `${nombre}, p` : 'P'}reparamos ${titleCount} para ${goal}`

    const contactHref = (message: string) => {
        if (empresa?.whatsapp) return `https://wa.me/${empresa.whatsapp}?text=${encodeURIComponent(message)}`
        if (empresa?.correos[0]) return `mailto:${empresa.correos[0]}?subject=${encodeURIComponent(`Cotización n.º ${numero}`)}&body=${encodeURIComponent(message)}`
        return null
    }

    const contactoMessage = selected && selectedPrice
        ? `Hola, me interesa la propuesta de la cotización n.º ${numero}: ${selected.cia} · ${selected.plan}${selectedPrice.total != null ? ` · ${selectedPrice.precio} (${frecuenciaLabel.toLowerCase()})` : ''}. ¿Me ayudas a contratarla?`
        : `Hola, tengo dudas sobre la cotización n.º ${numero}.`

    const fields = ramoFields(ramo)

    return {
        cotizacion,
        loading,
        error,
        empresa,
        numero,
        ramo: ramo || 'Seguro',
        vigencia,
        title,
        intro: options.length > 1
            ? 'Comparamos aseguradoras para encontrar la mejor relación entre protección y precio. Revisa cada opción y elige la que prefieras.'
            : 'Revisa los detalles de la propuesta y, si te convence, contacta a tu agente para contratarla.',
        fields,
        frecuencias,
        frecuencia,
        pickFrecuencia: setFrecuenciaPick,
        options: options.map((option, index) => ({
            ...option,
            price: prices[index],
            on: index === resolvedIndex,
            cheapest: options.length > 1 && index === cheapestIndex,
        })),
        pickOption: (id: number) => {
            const index = options.findIndex((option) => option.id === id)
            if (index >= 0 && prices[index].available) setSelectedId(id)
        },
        selected,
        selectedPrice,
        frecuenciaLabel,
        sumaLabel,
        beneficios: selected ? [...selected.campos, ...(fields.caracteristicas ? selected.caracteristicas : [])] : [],
        asegurados: (cotizacion?.asegurados || []).map(quoteInsured).map((item) => ({
            ...item,
            iniciales: initials(item.nombre),
            detalle: [item.parentesco, item.genero, item.edad != null ? `${item.edad} años` : ''].filter(Boolean).join(' · '),
        })),
        vehiculos: (cotizacion?.vehiculos || []).map((item, index) => ({
            key: String(item.id ?? index),
            descripcion: text(item.descripcion) || '—',
            modelo: item.modelo != null ? String(item.modelo) : '',
        })),
        bien: text(cotizacion?.texto),
        corregirHref: contactHref(`Hola, quiero corregir un dato de la cotización n.º ${numero}.`),
        contactoHref: contactHref(contactoMessage),
        contactoWhatsapp: Boolean(empresa?.whatsapp),
        descargar: () => window.print(),
        year: currentYear(),
    }
}
